/**
 * Das Galaktische Credit-Casino (GDD §4).
 *
 * Purpose per the GDD: skim surplus Galactic Credits out of the economy
 * ("Credit-Sink") and strengthen daily retention. Two games ship here — the
 * Sci-Fi slot machine and High-Low price betting; Asteroiden-Roulette is
 * listed in docs/ROADMAP.md.
 *
 * Fair-play rules baked in:
 *  - The daily stake limit is tied to account level, exactly as the GDD
 *    requires ("um Missbrauch durch Neulinge zu verhindern").
 *  - House edge is a stated constant, not an emergent accident: both games
 *    pay back ~92 % in expectation, so credits drain slowly rather than
 *    evaporating.
 *  - Every roll advances the saved RNG seed, so outcomes are reproducible
 *    from a save and a future server can verify a client-reported win.
 */

import type { CasinoState, GameState } from './types.js';
import { nextInt, nextRandom } from './rng.js';

/** Daily stake ceiling: 2 000 credits per account level (design fill-in). */
export function dailyStakeLimit(level: number): number {
  return 2_000 * level;
}

/** Minimum wager — keeps the log readable and blocks 1-credit grinding. */
export const MIN_STAKE = 50;

export type CasinoGame = 'slots' | 'highlow' | 'roulette';

/**
 * Casino building level required to unlock each game (owner decision — a
 * player who never builds a Händler-Relaisstation can no longer reach the
 * casino at all, see `requires.buildings.tradeRelay` on the `casino`
 * building in data/buildings.ts, which closes the credit dead-end where
 * starting credits get gambled away with no market to earn more).
 * Cheapest/simplest game unlocks first, design fill-in for the exact split.
 */
export const CASINO_GAME_MIN_LEVEL: Record<CasinoGame, number> = {
  slots: 1,
  highlow: 2,
  roulette: 3,
};

export function createCasino(now: number): CasinoState {
  return { stakedToday: 0, dayStartedAt: now, totalStaked: 0, totalWon: 0 };
}

/** Roll the 24 h window forward if it has elapsed. Mutates. */
export function rolloverDay(state: GameState, now: number): void {
  const elapsed = now - state.casino.dayStartedAt;
  if (elapsed >= 86_400_000) {
    const days = Math.floor(elapsed / 86_400_000);
    state.casino.dayStartedAt += days * 86_400_000;
    state.casino.stakedToday = 0;
  }
}

/** Credits the player may still stake today. */
export function remainingStake(state: GameState, now: number): number {
  const elapsed = now - state.casino.dayStartedAt;
  const staked = elapsed >= 86_400_000 ? 0 : state.casino.stakedToday;
  return Math.max(0, dailyStakeLimit(state.level) - staked);
}

/* ------------------------------------------------------------------ *
 * Slot machine                                                        *
 * ------------------------------------------------------------------ */

/** Reel symbols, rarest last — the payout table keys off triples and pairs. */
export const SLOT_SYMBOLS = ['titan', 'silicate', 'isotope', 'aether', 'chronos'] as const;
export type SlotSymbol = (typeof SLOT_SYMBOLS)[number];

/** Multiplier for three of a kind. */
const TRIPLE_PAYOUT: Record<SlotSymbol, number> = {
  titan: 5,
  silicate: 8,
  isotope: 12,
  aether: 25,
  chronos: 60,
};

/**
 * Pairs pay only for the three SCARCE symbols, at this multiple.
 *
 * Paying every pair looked generous on paper and was catastrophic in practice:
 * with these reel weights some pair shows up on 54 % of spins, which pushed the
 * machine to 132 % RTP — the casino would have printed credits instead of
 * draining them, inverting its entire purpose as the GDD §4 credit sink.
 * Restricting pairs to isotope/aether/chronos drops their frequency to ~15 %
 * and lands the machine at ≈92 % RTP. (Common-ore pairs paying nothing also
 * reads correctly: nobody gets rich on two lumps of titan.)
 */
const PAIR_PAYOUT = 2.7;
const PAIR_ELIGIBLE: ReadonlySet<SlotSymbol> = new Set(['isotope', 'aether', 'chronos']);

export interface SlotResult {
  reels: [SlotSymbol, SlotSymbol, SlotSymbol];
  /** Credits paid out (0 on a loss). */
  payout: number;
  /** 'triple' | 'pair' | 'none' — drives the UI reaction. */
  kind: 'triple' | 'pair' | 'none';
}

/**
 * Spin three reels. Weighted so rare symbols are genuinely rare: the weights
 * below give ≈92 % return to player against the payout table above.
 */
const SLOT_WEIGHTS: Record<SlotSymbol, number> = {
  titan: 34,
  silicate: 27,
  isotope: 20,
  aether: 13,
  chronos: 6,
};
const WEIGHT_TOTAL = Object.values(SLOT_WEIGHTS).reduce((a, b) => a + b, 0);

function rollReel(seed: number): { symbol: SlotSymbol; seed: number } {
  const roll = nextInt(seed, 1, WEIGHT_TOTAL);
  let remaining = roll.value;
  for (const symbol of SLOT_SYMBOLS) {
    remaining -= SLOT_WEIGHTS[symbol];
    if (remaining <= 0) return { symbol, seed: roll.seed };
  }
  return { symbol: 'titan', seed: roll.seed };
}

/** Pure spin — mutates only the passed seed value, returns the new one. */
export function spinSlots(seed: number, stake: number): { result: SlotResult; seed: number } {
  const a = rollReel(seed);
  const b = rollReel(a.seed);
  const c = rollReel(b.seed);
  const reels: [SlotSymbol, SlotSymbol, SlotSymbol] = [a.symbol, b.symbol, c.symbol];

  let payout = 0;
  let kind: SlotResult['kind'] = 'none';
  if (a.symbol === b.symbol && b.symbol === c.symbol) {
    payout = Math.floor(stake * TRIPLE_PAYOUT[a.symbol]);
    kind = 'triple';
  } else {
    const paired =
      a.symbol === b.symbol || a.symbol === c.symbol
        ? a.symbol
        : b.symbol === c.symbol
          ? b.symbol
          : null;
    if (paired && PAIR_ELIGIBLE.has(paired)) {
      payout = Math.floor(stake * PAIR_PAYOUT);
      kind = 'pair';
    }
  }

  return { result: { reels, payout, kind }, seed: c.seed };
}

/* ------------------------------------------------------------------ *
 * High-Low — betting on resource price movement (GDD §4)              *
 * ------------------------------------------------------------------ */

export interface HighLowResult {
  /** Reference price index, 1..100. */
  current: number;
  next: number;
  guess: 'higher' | 'lower';
  won: boolean;
  payout: number;
}

/** Correct guess pays 1.84× the stake (≈92 % RTP at even odds). */
const HIGHLOW_PAYOUT = 1.84;

export function playHighLow(
  seed: number,
  stake: number,
  guess: 'higher' | 'lower',
  current: number,
): { result: HighLowResult; seed: number } {
  const roll = nextInt(seed, 1, 100);
  const next = roll.value;
  // A tie resolves against the house's favour-free rule: player loses only on
  // a strict miss, so an exact tie counts as a win.
  const won = guess === 'higher' ? next >= current : next <= current;
  return {
    result: {
      current,
      next,
      guess,
      won,
      payout: won ? Math.floor(stake * HIGHLOW_PAYOUT) : 0,
    },
    seed: roll.seed,
  };
}

/** Fresh price index for the next High-Low round. */
export function rollPriceIndex(seed: number): { value: number; seed: number } {
  const roll = nextRandom(seed);
  // Keep it off the extremes so neither guess is close to a sure thing.
  return { value: 20 + Math.floor(roll.value * 61), seed: roll.seed };
}

/* ------------------------------------------------------------------ *
 * Asteroiden-Roulette — the third game the GDD §4 names                *
 * ------------------------------------------------------------------ */

/**
 * The wheel has 13 sectors: 12 playable ones split evenly between the two ore
 * types, plus a single "Leere" (void) sector that belongs to neither. The void
 * is the entire house edge on the colour bet — the same job the green zero
 * does on a real roulette wheel — which keeps the maths legible instead of
 * hiding the edge inside a fudged payout.
 */
export const ROULETTE_SECTORS = 13;
export const ROULETTE_VOID = 0;

export type RouletteBet =
  | { kind: 'ore'; ore: 'titan' | 'silicate' }
  | { kind: 'sector'; sector: number };

/** Sectors 1..12 alternate between the two ores; sector 0 is the void. */
export function sectorOre(sector: number): 'titan' | 'silicate' | 'void' {
  if (sector === ROULETTE_VOID) return 'void';
  return sector % 2 === 1 ? 'titan' : 'silicate';
}

/** Even-money colour bet. 6/13 chance × 2.0 → ≈92.3 % RTP. */
const ROULETTE_ORE_PAYOUT = 2;
/** Single sector: 1/13 chance × 12.0 → ≈92.3 % RTP, matching the colour bet. */
const ROULETTE_SECTOR_PAYOUT = 12;

export interface RouletteResult {
  sector: number;
  ore: 'titan' | 'silicate' | 'void';
  bet: RouletteBet;
  won: boolean;
  payout: number;
}

export function spinRoulette(
  seed: number,
  stake: number,
  bet: RouletteBet,
): { result: RouletteResult; seed: number } {
  const roll = nextInt(seed, 0, ROULETTE_SECTORS - 1);
  const sector = roll.value;
  const ore = sectorOre(sector);

  const won =
    bet.kind === 'ore' ? ore === bet.ore : sector === bet.sector;
  const payout = won
    ? Math.floor(stake * (bet.kind === 'ore' ? ROULETTE_ORE_PAYOUT : ROULETTE_SECTOR_PAYOUT))
    : 0;

  return { result: { sector, ore, bet, won, payout }, seed: roll.seed };
}

/**
 * Chance that a spin also drops a Void-Phantom blueprint.
 *
 * The GDD lists "Baupläne für Event-Schiffe" among casino prizes, and the ship
 * itself is flagged event-only in the catalogue — this is the only way it
 * enters a fleet. Deliberately rare: at 0.4 % a player sees roughly one per
 * 250 spins, which keeps the Void-Phantom a story rather than a staple.
 */
export const BLUEPRINT_DROP_CHANCE = 0.004;

export function rollBlueprint(seed: number): { dropped: boolean; seed: number } {
  const roll = nextRandom(seed);
  return { dropped: roll.value < BLUEPRINT_DROP_CHANCE, seed: roll.seed };
}
